import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

const elements = {
  canvas: document.querySelector("#canvas"),
  viewport: document.querySelector("#viewport"),
  loading: document.querySelector("#loading"),
  empty: document.querySelector("#empty-state"),
  list: document.querySelector("#model-list"),
  search: document.querySelector("#search-input"),
  package: document.querySelector("#package-select"),
  resultCount: document.querySelector("#result-count"),
  archiveCount: document.querySelector("#archive-count"),
  gridButton: document.querySelector("#grid-button"),
  wireframeButton: document.querySelector("#wireframe-button"),
  frameButton: document.querySelector("#frame-button"),
  copyLink: document.querySelector("#copy-link-button"),
  toast: document.querySelector("#toast"),
  modelPackage: document.querySelector("#model-package"),
  modelName: document.querySelector("#model-name"),
  modelPath: document.querySelector("#model-path"),
  triangles: document.querySelector("#stat-triangles"),
  vertices: document.querySelector("#stat-vertices"),
  frames: document.querySelector("#stat-frames"),
  materials: document.querySelector("#stat-materials"),
  sequence: document.querySelector("#detail-sequence"),
  frame: document.querySelector("#detail-frame"),
  inspectorTabs: [...document.querySelectorAll(".inspector-tab")],
  inspectorPanels: [...document.querySelectorAll(".inspector-panel")],
};

const renderer = new THREE.WebGLRenderer({ canvas: elements.canvas, antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.15;

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(42, 1, 0.01, 100000);
camera.position.set(4, 3, 6);

const controls = new OrbitControls(camera, elements.canvas);
controls.enableDamping = true;
controls.dampingFactor = 0.075;
controls.screenSpacePanning = true;

scene.add(new THREE.HemisphereLight(0xdde4ff, 0x302b20, 2.2));
const keyLight = new THREE.DirectionalLight(0xffffff, 3.2);
keyLight.position.set(4, 8, 6);
scene.add(keyLight);
const rimLight = new THREE.DirectionalLight(0xc6e75b, 1.8);
rimLight.position.set(-5, 3, -4);
scene.add(rimLight);

const grid = new THREE.GridHelper(20, 20, 0x626252, 0x35352f);
grid.material.transparent = true;
grid.material.opacity = 0.45;
scene.add(grid);

const loader = new GLTFLoader();
const numberFormat = new Intl.NumberFormat("en-US");
let models = [];
let filteredModels = [];
let currentRecord = null;
let currentRoot = null;
let loadToken = 0;
let wireframe = false;
let toastTimer = null;

function parseCsv(text) {
  const [headerLine, ...lines] = text.trim().split(/\r?\n/);
  const headers = headerLine.split(",");
  return lines.filter(Boolean).map((line) => {
    const values = line.split(",");
    return Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ""]));
  });
}

function modelUrl(record) {
  const safePath = record.glb.split("/").map(encodeURIComponent).join("/");
  return new URL(`/models/${safePath}`, window.location.origin).href;
}

function showToast(message, isError = false) {
  clearTimeout(toastTimer);
  elements.toast.textContent = message;
  elements.toast.style.borderColor = isError ? "var(--danger)" : "";
  elements.toast.classList.add("is-visible");
  toastTimer = setTimeout(() => elements.toast.classList.remove("is-visible"), 2200);
}

function disposeCurrentModel() {
  if (!currentRoot) return;
  scene.remove(currentRoot);
  currentRoot.traverse((child) => {
    child.geometry?.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.filter(Boolean).forEach((material) => {
      Object.values(material).forEach((value) => value?.isTexture && value.dispose());
      material.dispose();
    });
  });
  currentRoot = null;
}

function applyWireframe() {
  currentRoot?.traverse((child) => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.filter(Boolean).forEach((material) => {
      material.wireframe = wireframe;
      material.needsUpdate = true;
    });
  });
}

function frameModel() {
  if (!currentRoot) return;
  const box = new THREE.Box3().setFromObject(currentRoot);
  if (box.isEmpty()) return;
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const radius = Math.max(sphere.radius, 0.01);
  const distance = radius / Math.sin(THREE.MathUtils.degToRad(camera.fov * 0.5));
  const direction = new THREE.Vector3(1, 0.65, 1).normalize();
  controls.target.copy(sphere.center);
  camera.position.copy(sphere.center).addScaledVector(direction, distance * 1.18);
  camera.near = Math.max(radius / 1000, 0.001);
  camera.far = Math.max(radius * 100, 100);
  camera.updateProjectionMatrix();
  controls.maxDistance = radius * 30;
  controls.update();
  grid.position.y = box.min.y;
  grid.scale.setScalar(Math.max(radius / 5, 0.1));
}

function updateInspector(record, root) {
  const meshes = [];
  root.traverse((child) => child.isMesh && meshes.push(child));
  const materialCount = new Set(meshes.flatMap((mesh) => Array.isArray(mesh.material) ? mesh.material : [mesh.material])).size;
  elements.modelPackage.textContent = record?.package || "Local file";
  elements.modelName.textContent = record?.mesh || root.name || "Dropped model";
  elements.modelPath.textContent = record?.glb || "Archive model";
  elements.triangles.textContent = record ? numberFormat.format(Number(record.triangles)) : "—";
  elements.vertices.textContent = record ? numberFormat.format(Number(record.modern_vertices)) : "—";
  elements.frames.textContent = record ? numberFormat.format(Number(record.frames)) : "—";
  elements.materials.textContent = numberFormat.format(materialCount);
  elements.sequence.textContent = record?.selected_sequence || "—";
  elements.frame.textContent = record ? `${record.selected_frame} / ${Number(record.frames) - 1}` : "—";
}

function setQuery(record) {
  const url = new URL(window.location.href);
  if (record) url.searchParams.set("model", `${record.package}/${record.mesh}`);
  else url.searchParams.delete("model");
  history.replaceState(null, "", url);
}

async function loadModel(url, record = null) {
  const token = ++loadToken;
  elements.loading.hidden = false;
  elements.empty.hidden = true;
  try {
    const gltf = await loader.loadAsync(url);
    if (token !== loadToken) return;
    disposeCurrentModel();
    currentRoot = gltf.scene;
    currentRecord = record;
    scene.add(currentRoot);
    applyWireframe();
    updateInspector(record, currentRoot);
    frameModel();
    setQuery(record);
    renderList();
  } catch (error) {
    console.error(error);
    showToast(`Could not load ${record?.mesh || "GLB"}`, true);
  } finally {
    if (token === loadToken) elements.loading.hidden = true;
  }
}

function renderList() {
  elements.resultCount.textContent = numberFormat.format(filteredModels.length);
  elements.empty.hidden = filteredModels.length !== 0;
  const fragment = document.createDocumentFragment();
  filteredModels.forEach((record) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "model-item";
    button.role = "option";
    button.title = `${record.package} / ${record.mesh}`;
    button.setAttribute("aria-selected", String(currentRecord === record));
    if (currentRecord === record) button.classList.add("is-selected");

    const name = document.createElement("span");
    name.className = "model-item-name";
    name.textContent = record.mesh;
    const count = document.createElement("span");
    count.className = "model-item-count";
    count.textContent = `${numberFormat.format(Number(record.triangles))}△`;
    button.append(name, count);
    button.addEventListener("click", () => loadModel(modelUrl(record), record));
    fragment.append(button);
  });
  elements.list.replaceChildren(fragment);
  elements.list.querySelector(".is-selected")?.scrollIntoView({ block: "nearest" });
}

function applyFilters() {
  const query = elements.search.value.trim().toLowerCase();
  const packageName = elements.package.value;
  filteredModels = models.filter((record) => {
    return (!packageName || record.package === packageName)
      && (!query || record.mesh.toLowerCase().includes(query) || record.package.toLowerCase().includes(query));
  });
  renderList();
}

async function initialize() {
  try {
    const response = await fetch("/models/manifest.csv");
    if (!response.ok) throw new Error(`Manifest request failed: ${response.status}`);
    models = parseCsv(await response.text()).sort((a, b) => a.mesh.localeCompare(b.mesh, undefined, { numeric: true }));
    filteredModels = models;
    elements.archiveCount.textContent = numberFormat.format(models.length);

    [...new Set(models.map((record) => record.package))].sort().forEach((packageName) => {
      const option = document.createElement("option");
      option.value = packageName;
      option.textContent = `${packageName} (${models.filter((record) => record.package === packageName).length})`;
      elements.package.append(option);
    });

    const requested = new URLSearchParams(location.search).get("model");
    const initial = models.find((record) => `${record.package}/${record.mesh}` === requested) || models[0];
    renderList();
    if (initial) loadModel(modelUrl(initial), initial);
  } catch (error) {
    console.error(error);
    elements.empty.hidden = false;
    elements.empty.querySelector("strong").textContent = "Archive unavailable";
    elements.empty.querySelector("span").textContent = "Start the viewer with serve.ps1, not as a file URL.";
  }
}

function setPressed(button, pressed) {
  button.classList.toggle("is-active", pressed);
  button.setAttribute("aria-pressed", String(pressed));
}

function selectInspectorTab(tab) {
  elements.inspectorTabs.forEach((button) => {
    const selected = button === tab;
    button.classList.toggle("is-active", selected);
    button.setAttribute("aria-selected", String(selected));
  });
  elements.inspectorPanels.forEach((panel) => {
    const selected = panel.id === tab.getAttribute("aria-controls");
    panel.classList.toggle("is-active", selected);
    panel.hidden = !selected;
  });
}

elements.search.addEventListener("input", applyFilters);
elements.package.addEventListener("change", applyFilters);
elements.inspectorTabs.forEach((tab) => tab.addEventListener("click", () => selectInspectorTab(tab)));
elements.frameButton.addEventListener("click", frameModel);
elements.gridButton.addEventListener("click", () => {
  grid.visible = !grid.visible;
  setPressed(elements.gridButton, grid.visible);
});
elements.wireframeButton.addEventListener("click", () => {
  wireframe = !wireframe;
  setPressed(elements.wireframeButton, wireframe);
  applyWireframe();
});
elements.copyLink.addEventListener("click", async () => {
  try {
    await navigator.clipboard.writeText(location.href);
    showToast("Model link copied");
  } catch {
    showToast("Could not access clipboard", true);
  }
});

window.addEventListener("keydown", (event) => {
  if (event.key === "/" && document.activeElement !== elements.search) {
    event.preventDefault();
    elements.search.focus();
  }
  if (event.key.toLowerCase() === "f" && !["INPUT", "SELECT"].includes(document.activeElement.tagName)) frameModel();
  if (event.key === "Escape") elements.search.blur();
});

const resizeObserver = new ResizeObserver(() => {
  const { width, height } = elements.viewport.getBoundingClientRect();
  if (!width || !height) return;
  renderer.setSize(width, height, false);
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
});
resizeObserver.observe(elements.viewport);

renderer.setAnimationLoop(() => {
  controls.update();
  renderer.render(scene, camera);
});

initialize();
